import { useMemo } from 'react';

import { useSelfServe } from 'api/useSelfServe';
import {
  EPublisherSolutionModel,
  ESelfServeTaskStatus,
  ETaskId,
  SelfServeTask
} from 'common/contracts';
import { User } from 'common/contracts';
import { EEventsType } from 'constants/enums';
import { ELocalStorageKeys } from 'constants/enums';
import {
  checklistInitialCategories,
  getCategoryStatus
} from 'pages/Layout/SetupChecklist/constants';
import { ISetupChecklistCategory } from 'pages/Layout/SetupChecklist/types';
import { localStorageUtil } from 'utils/localStorageUtil';
import { trackMixpanelEvent } from 'utils/mixpanelClient';

import { useObservable } from '../observable';
import { setupChecklistStateService } from '../setup-checklist.state.service';

export const useChecklistState = () => {
  const categories = useObservable(setupChecklistStateService.categories);
  const isExpanded = useObservable(setupChecklistStateService.isExpanded);
  const userDetails = localStorageUtil.getAny<User>(
    ELocalStorageKeys.USER_DETAILS
  );

  const publisherId = useMemo(
    () =>
      userDetails?.projects?.find(
        ({ projectType }) => projectType === EPublisherSolutionModel.CHECKOUT
      )?.publisherId,
    [userDetails?.projects]
  );

  const { updateTask, getTasks } = useSelfServe(publisherId);

  const setTasksFromStatusList = (statusList?: SelfServeTask[]) => {
    if (!statusList || !statusList.length) {
      setupChecklistStateService.setCategories([]);
      return;
    }

    const taskStatusMap = Object.fromEntries(
      statusList.map((task) => [task.id, task.status])
    );

    const populatedCategories = checklistInitialCategories
      .map((section) => {
        const validTasks = section.tasks
          .filter((task) => taskStatusMap[task.id])
          .map((task) => ({
            ...task,
            status: taskStatusMap[task.id]
          }));

        if (validTasks.length === 0) return null;

        const taskStatuses = validTasks.map((t) => t.status);
        const sectionStatus = getCategoryStatus(taskStatuses);

        return {
          ...section,
          tasks: validTasks,
          status: sectionStatus
        };
      })
      .filter(Boolean);

    setupChecklistStateService.setCategories(
      populatedCategories as ISetupChecklistCategory[]
    );
  };

  const hasCategories = useMemo(() => {
    return !!categories?.length && categories.length > 0;
  }, [categories]);

  const setTaskStatus = (id: ETaskId, status?: ESelfServeTaskStatus) => {
    const targetStatus = status || ESelfServeTaskStatus.DONE;
    
    updateTask.mutate(
      { id, status: targetStatus },
      {
        onSuccess: () => {
          if (targetStatus === ESelfServeTaskStatus.DONE) {
            const taskTitle = categories
              ?.flatMap(category => category.tasks)
              ?.find(task => task.id === id)?.title || id;
            
            trackMixpanelEvent(EEventsType.DASHBOARD_SETUP_GUIDE_TASK_COMPLETED, {
              task_name: taskTitle,
              completion_time: new Date().toISOString()
            });
          }
          
          getTasks.refetch();
        }
      }
    );
  };

  return {
    hasCategories,
    categories,
    isExpanded,
    selectedCategory: useObservable(
      setupChecklistStateService.selectedCategory
    ),
    isSendAgreementModalOpen: useObservable(
      setupChecklistStateService.isSendAgreementModalOpen
    ),
    isVerifyBusinessModalOpen: useObservable(
      setupChecklistStateService.isVerifyBusinessModalOpen
    ),
    setIsExpanded: setupChecklistStateService.setIsExpanded,
    setIsVerifyBusinessModalOpen:
      setupChecklistStateService.setIsVerifyBusinessModalOpen,
    setIsSendAgreementModalOpen:
      setupChecklistStateService.setIsSendAgreementModalOpen,
    setSelectedCategory: setupChecklistStateService.setSelectedCategory,
    setTasksFromStatusList,
    setTaskStatus,
    toggleChecklistExpandedState: (forceState?: boolean) =>
      setupChecklistStateService.toggleChecklistExpandedState(
        getTasks.refetch,
        forceState
      ),
    resetSelectedCategory: setupChecklistStateService.resetSelectedCategory
  };
};
